import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, orderBy, onSnapshot, addDoc, serverTimestamp, updateDoc, doc, getDocs, deleteDoc, writeBatch } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useAuthStore } from '../store';
import { MessageCircle, Send, User, Store, Trash2, Shield } from 'lucide-react';
import { toast } from 'sonner';

interface ChatThread {
  id: string;
  customerId: string;
  customerName: string;
  vendorId: string;
  vendorName: string;
  lastMessage: string;
  lastMessageAt: any;
  readStatus: Record<string, boolean>;
}

interface ChatMessage {
  id: string;
  threadId: string;
  senderId: string;
  content: string;
  createdAt: any;
}

export function Messaging({ role }: { role: 'customer' | 'vendor' | 'admin' }) {
  const { profile } = useAuthStore();
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [activeThread, setActiveThread] = useState<ChatThread | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!profile || !auth.currentUser) return;
    
    const uid = auth.currentUser.uid;
    let q;
    if (role === 'vendor') {
      q = query(collection(db, 'chat_threads'), where('vendorId', '==', uid));
    } else if (role === 'admin') {
      q = query(collection(db, 'chat_threads'));
    } else {
      q = query(collection(db, 'chat_threads'), where('customerId', '==', uid));
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedThreads = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as ChatThread));
      fetchedThreads.sort((a, b) => {
        const timeA = a.lastMessageAt?.toMillis?.() || 0;
        const timeB = b.lastMessageAt?.toMillis?.() || 0;
        return timeB - timeA; // Descending
      });
      setThreads(fetchedThreads);
      if (fetchedThreads.length > 0) {
        // Find existing or default to first
        setActiveThread(prev => {
          if (prev && fetchedThreads.find(t => t.id === prev.id)) {
            return prev;
          }
          return fetchedThreads[0];
        });
      } else {
        setActiveThread(null);
      }
    });

    return () => unsubscribe();
  }, [profile, role]);

  useEffect(() => {
    if (!activeThread) return;

    const q = query(
      collection(db, 'chat_threads', activeThread.id, 'messages')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedMessages = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as ChatMessage));
      fetchedMessages.sort((a, b) => {
        const timeA = a.createdAt?.toMillis?.() || 0;
        const timeB = b.createdAt?.toMillis?.() || 0;
        return timeA - timeB; // Ascending
      });
      setMessages(fetchedMessages);
      
      // Mark thread as read
      if (auth.currentUser) {
        const updates: any = {};
        if (!activeThread.readStatus?.[auth.currentUser.uid]) {
          updates[`readStatus.${auth.currentUser.uid}`] = true;
        }
        if (role === 'admin' && !activeThread.readStatus?.['ADMIN']) {
          updates[`readStatus.ADMIN`] = true;
        }
        if (Object.keys(updates).length > 0) {
          updateDoc(doc(db, 'chat_threads', activeThread.id), updates)
            .catch(err => console.error("Could not update read status:", err));
        }
      }
    });

    return () => unsubscribe();
  }, [activeThread]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeThread || !auth.currentUser) return;

    try {
      const content = newMessage.trim();
      setNewMessage('');

      await addDoc(collection(db, 'chat_threads', activeThread.id, 'messages'), {
        threadId: activeThread.id,
        senderId: auth.currentUser.uid,
        content,
        createdAt: serverTimestamp()
      });

      let otherId;
      if (role === 'admin') {
        otherId = activeThread.vendorId === 'ADMIN' ? activeThread.customerId : activeThread.vendorId;
      } else if (role === 'vendor') {
        otherId = activeThread.customerId;
      } else {
        otherId = activeThread.vendorId;
      }

      const updates: any = {
        lastMessage: content,
        lastMessageAt: serverTimestamp(),
        [`readStatus.${otherId}`]: false
      };
      
      if (role === 'admin') {
        updates[`readStatus.ADMIN`] = true;
      } else {
        updates[`readStatus.${auth.currentUser.uid}`] = true;
      }

      await updateDoc(doc(db, 'chat_threads', activeThread.id), updates);
    } catch (err) {
      console.error("Error sending message:", err);
      toast.error("Failed to send message");
    }
  };

  const handleContactAdmin = async () => {
    if (!auth.currentUser || !profile) return;
    
    // Check if thread to ADMIN already exists
    let existing;
    if (role === 'vendor') {
      existing = threads.find(t => t.customerId === 'ADMIN');
    } else if (role === 'customer') {
      existing = threads.find(t => t.vendorId === 'ADMIN');
    }

    if (existing) {
      setActiveThread(existing);
      return;
    }

    try {
      const threadData: any = {
        lastMessage: '',
        lastMessageAt: serverTimestamp(),
        readStatus: {
          [auth.currentUser.uid]: true,
          'ADMIN': false
        }
      };
      
      if (role === 'vendor') {
        threadData.customerId = 'ADMIN';
        threadData.customerName = 'Platform Support';
        threadData.vendorId = auth.currentUser.uid;
        threadData.vendorName = profile.storeName || profile.displayName || 'Vendor';
      } else if (role === 'customer') {
        threadData.vendorId = 'ADMIN';
        threadData.vendorName = 'Platform Support';
        threadData.customerId = auth.currentUser.uid;
        threadData.customerName = profile.displayName || 'Customer';
      }

      await addDoc(collection(db, 'chat_threads'), threadData);
      toast.success("Created support chat");
    } catch (e) {
      console.error(e);
      toast.error('Could not start admin chat');
    }
  };

  const handleDeleteThread = async (threadId: string) => {
    if (!auth.currentUser) return;
    if (!confirm('Are you sure you want to delete this conversation? This will delete it for the other participant as well.')) return;

    try {
      // 1. Delete all messages
      const msgsData = await getDocs(collection(db, 'chat_threads', threadId, 'messages'));
      const batch = writeBatch(db);
      msgsData.docs.forEach(d => {
        batch.delete(d.ref);
      });
      await batch.commit();

      // 2. Delete the thread document
      await deleteDoc(doc(db, 'chat_threads', threadId));
      if (activeThread?.id === threadId) {
        setActiveThread(null);
      }
      toast.success("Conversation deleted");
    } catch (error) {
      console.error("Error deleting thread", error);
      toast.error("Failed to delete thread");
    }
  };

  const getOtherName = (thread: ChatThread) => {
    if (role === 'vendor') return thread.customerName;
    if (role === 'admin') {
      if (thread.vendorId === 'ADMIN') return thread.customerName;
      if (thread.customerId === 'ADMIN') return thread.vendorName;
      return `${thread.customerName} & ${thread.vendorName}`;
    }
    return thread.vendorName;
  };

  const hasUnread = (thread: ChatThread) => {
    // For admin, check if "ADMIN" has false, OR if admin's uid has false
    if (role === 'admin' && thread.readStatus?.['ADMIN'] === false) return true;
    return auth.currentUser ? !thread.readStatus?.[auth.currentUser.uid] : false;
  };

  return (
    <div className="flex h-[600px] border border-neutral-200 dark:border-neutral-800 rounded-2xl overflow-hidden bg-white dark:bg-neutral-900 shadow-sm">
      {/* Threads List */}
      <div className="w-1/3 border-r border-neutral-200 dark:border-neutral-800 flex flex-col bg-neutral-50/50 dark:bg-neutral-900/50">
        <div className="p-4 border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 flex justify-between items-center">
          <h2 className="font-medium text-neutral-900 dark:text-white flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-neutral-500" />
            Inbox
          </h2>
          {(role === 'vendor' || role === 'customer') && (
            <button 
              onClick={handleContactAdmin}
              className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 transition-colors"
            >
              Contact Support
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          {threads.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full p-8 text-center text-neutral-400 dark:text-neutral-500">
               <MessageCircle className="w-8 h-8 mb-3 stroke-[1.5]" />
               <p className="text-sm">No messages yet.</p>
            </div>
          ) : (
            threads.map(thread => (
              <button
                key={thread.id}
                onClick={() => setActiveThread(thread)}
                className={`w-full text-left p-4 border-b border-neutral-200 dark:border-neutral-800 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors ${activeThread?.id === thread.id ? 'bg-white dark:bg-neutral-800' : ''}`}
              >
                <div className="flex justify-between items-start mb-1">
                  <span className={`font-medium text-sm truncate pr-2 ${hasUnread(thread) ? 'text-neutral-900 dark:text-white' : 'text-neutral-700 dark:text-neutral-300'} ${thread.customerId === 'ADMIN' ? 'text-indigo-600 dark:text-indigo-400 font-bold' : ''}`}>
                    {getOtherName(thread)}
                  </span>
                  {hasUnread(thread) && <span className="w-2.5 h-2.5 bg-indigo-500 rounded-full shrink-0" />}
                </div>
                <p className={`text-xs truncate ${hasUnread(thread) ? 'text-neutral-700 dark:text-neutral-300 font-medium' : 'text-neutral-500 dark:text-neutral-400'}`}>
                  {thread.lastMessage || 'Connected'}
                </p>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="w-2/3 flex flex-col bg-white dark:bg-neutral-950">
        {activeThread ? (
          <>
            <div className="p-4 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between bg-white dark:bg-neutral-900">
              <div className="flex items-center gap-3">
                {activeThread.customerId === 'ADMIN' ? (
                  <Shield className="w-5 h-5 text-indigo-500" />
                ) : role === 'vendor' ? (
                  <User className="w-5 h-5 text-neutral-400" />
                ) : (
                  <Store className="w-5 h-5 text-neutral-400" />
                )}
                <h3 className="font-medium text-neutral-900 dark:text-white text-sm">{getOtherName(activeThread)}</h3>
              </div>
              <button
                onClick={() => handleDeleteThread(activeThread.id)}
                className="p-2 text-neutral-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors"
                title="Delete Conversation"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg) => {
                const isMine = msg.senderId === auth.currentUser?.uid;
                return (
                  <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${isMine ? 'bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 rounded-br-sm' : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-900 dark:text-white rounded-bl-sm'}`}>
                      <p className="text-sm">{msg.content}</p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
            {((role !== 'admin') || (activeThread.customerId === 'ADMIN' || activeThread.vendorId === 'ADMIN')) ? (
              <form onSubmit={handleSendMessage} className="p-4 bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-800 flex gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 px-4 py-2 bg-neutral-50 dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 rounded-full text-sm focus:outline-none focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white"
                />
                <button 
                  type="submit"
                  disabled={!newMessage.trim()}
                  className="w-10 h-10 rounded-full bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 flex flex-col items-center justify-center disabled:opacity-50 transition-opacity shrink-0"
                >
                  <Send className="w-4 h-4 ml-0.5" />
                </button>
              </form>
            ) : (
              <div className="p-4 bg-neutral-50 dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-800 text-center">
                <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">You are viewing this conversation as an Administrator.</span>
              </div>
            )}
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-neutral-400 dark:text-neutral-500 gap-3">
            <MessageCircle className="w-12 h-12 stroke-[1.5]" />
            <p className="text-sm">Select a conversation to start messaging</p>
          </div>
        )}
      </div>
    </div>
  );
}
